<script lang="ts">
    import { DEFAULT_BOX } from '$lib/components/constants';

    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';
    import { getResponseBgColor } from '$lib/utils/getResponseBgColor';

    import Color from 'color';
    import { fade } from 'svelte/transition';
    import CountUp from 'svelte-countup';

    import { isGradientColor } from '../_utils/color';
    import AnswerIcon from '../_wrappers/AnswerIcon.svelte';
    import Text from '../Text/Text.View.svelte';

    import type { JSONContent } from '@tiptap/core';

    export let id = '';
    export let link = '';
    export let draft: JSONContent;
    export let emoji = ':smiley:';
    export let icon = '';
    export let showIcon = true;
    export let srcType = '';
    export let iconName = '';
    export let platform = '';
    export let iconColor = '#000000';
    export let svg = '';
    export let isAnimated = false;
    export let jsonUrl: string | null = null;
    export let color = 'black';
    export let bgColor = 'transparent';
    export let resultId = '';
    export let onClick: (resultId: string, id: string, link: string) => void;
    export let onMouseEnter: (resultId: string, id: string, link: string) => void;
    export let isResultVisible = false;
    export let responsePercentage: number | undefined = 0;

    const handleClick = () => {
        // Prevent clicks after results are shown
        if (isResultVisible) {
            return;
        }

        onClick(resultId, id, link);
    };

    const handleKeyPress = (event: KeyboardEvent) => {
        event.preventDefault();

        if (event.key === 'Enter' || event.key === ' ') {
            handleClick();
        }
    };

    const handleMouseEnter = () => {
        onMouseEnter(resultId, id, link);
    };

    const isGradient = isGradientColor(bgColor);

    const bgColorDark = isGradient ? '' : Color(bgColor).darken(0.1);

    const choiceItemBorderRadiusClass = getBorderRadius('choiceItem');

    const backgroundImageStyle = isGradient ? `background-image: ${bgColor};` : '';

    const responseBgColor = getResponseBgColor(bgColor);

    let resultBgWidth = 0;

    $: if (isResultVisible) {
        resultBgWidth = (responsePercentage ?? 0) * 100;
    }
</script>

<div
    class={cn(
        'fun-relative fun-box-border fun-flex fun-h-full fun-min-h-16 fun-w-full fun-cursor-pointer fun-select-none fun-items-center fun-gap-4 fun-overflow-hidden fun-bg-[var(--answerBgColor)] fun-bg-no-repeat fun-p-4 fun-shadow-text-answer fun-transition-all active:fun-scale-90 active:fun-duration-200 sm:hover:fun-bg-[var(--answerBgColorDark)] sm:active:fun-bg-[var(--answerBgColorDark)]',
        choiceItemBorderRadiusClass,
        {
            'fun-bg-[length:100%] sm:fun-duration-500 sm:hover:fun-bg-[length:200%]': isGradient,
        },
    )}
    on:click={handleClick}
    on:mouseenter={handleMouseEnter}
    on:touchstart={handleMouseEnter}
    on:keypress={handleKeyPress}
    role="button"
    tabindex="0"
    style="--answerBgColor: {bgColor}; --answerBgColorDark: {bgColorDark}; {backgroundImageStyle}"
>
    <div
        class={cn(
            'fun-absolute fun-inset-0 fun-z-10 fun-size-full fun-transition-all fun-duration-700 [transition-timing-function:cubic-bezier(0.33,1,0.68,1)]',
            choiceItemBorderRadiusClass,
            responseBgColor,
        )}
        style="width: {resultBgWidth}%;"
    />

    {#if showIcon}
        <AnswerIcon
            {emoji}
            {icon}
            {srcType}
            {iconName}
            {platform}
            {iconColor}
            {svg}
            {isAnimated}
            {jsonUrl}
        />
    {/if}
    <span class="fun-flex-grow">
        <Text raw={draft} {color} box={DEFAULT_BOX} transitionEffect="none" />
    </span>

    {#if isResultVisible}
        <div
            class="font-inter fun-relative fun-z-10 fun-flex fun-h-full fun-items-center fun-justify-center fun-text-2xl fun-font-semibold fun-leading-8 fun-tracking-normal"
            style="color: {color}"
            in:fade
        >
            <CountUp
                initial={0}
                value={((responsePercentage || 0) * 100).toFixed(0)}
                duration={700}
                step={1}
                roundto={1}
            />%
        </div>
    {/if}
</div>
